/**
 * Returns the list of selectable warehouses for dropdowns.
 *
 * Mirrors the login-page list: every store appears regardless of is_group,
 * because some stores are group nodes in the warehouse tree and must still
 * be selectable everywhere. Only transit warehouses are excluded.
 * De-duplicates by warehouse name so the same store never appears twice.
 *
 * For stock movements (receipts, issues, transfers, adjustments) use
 * {@link getTransactionalWarehouses} — ERPNext rejects group nodes with
 * "Group node warehouse is not allowed to select for transactions".
 *
 * @param {Array} warehouses - raw warehouse list from the store
 * @returns {Array} cleaned, de-duplicated warehouses
 */
const isTransitWarehouse = (wh) => {
  if (!wh) return false;
  if ((wh.warehouse_type || '').toLowerCase() === 'transit') return true;
  const name = (wh.name || '').toLowerCase();
  const wname = (wh.warehouse_name || '').toLowerCase();
  return name.includes('goods in transit') || wname.includes('goods in transit');
};

/** True when the warehouse is a tree parent (cannot hold / post stock). */
export const isGroupWarehouse = (wh) => {
  if (!wh) return false;
  // Tessara stores are renamed ERPNext defaults — a store's internal id can
  // literally be "All Warehouses - TB" while it is a real, leaf warehouse
  // (is_group=0). Only the actual is_group flag tells us whether a
  // warehouse can hold/post stock; never infer it from the name/id.
  return wh.is_group === 1 || wh.is_group === true || wh.is_group === '1';
};

export const getSelectableWarehouses = (warehouses) => {
  if (!Array.isArray(warehouses)) return [];
  const seen = new Set();
  const result = [];
  for (const wh of warehouses) {
    if (!wh) continue;
    if (isTransitWarehouse(wh)) continue;
    const key = wh.name || wh.warehouse_name;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(wh);
  }
  return result;
};

/**
 * Warehouses that may be used on Stock Entry / Material Receipt / Issue /
 * Transfer lines. Excludes group nodes and transit.
 *
 * @param {Array} warehouses
 * @returns {Array}
 */
export const getTransactionalWarehouses = (warehouses) =>
  getSelectableWarehouses(warehouses).filter((wh) => !isGroupWarehouse(wh));

/**
 * Prefer the user's active/default warehouse when it is a leaf store;
 * otherwise the first transactional warehouse.
 *
 * @param {Array} warehouses
 * @param {object|string|null} preferred - warehouse object or name
 * @returns {string} warehouse name or ''
 */
export const resolveTransactionalWarehouseName = (warehouses, preferred = null) => {
  const list = getTransactionalWarehouses(warehouses);
  if (!list.length) return '';

  const preferredName =
    typeof preferred === 'string'
      ? preferred
      : preferred?.name || preferred?.warehouse_name || '';

  if (preferredName) {
    const match = list.find(
      (wh) => wh.name === preferredName || wh.warehouse_name === preferredName,
    );
    if (match) return match.name || match.warehouse_name || '';
  }
  return list[0].name || list[0].warehouse_name || '';
};
